// Shared photo strip used by the Web Report Editor (in-flight
// upload tiles) and the Detail screen (committed photo gallery).
//
// Goals:
//   - One component renders BOTH `UploadTask[]` (editor) and
//     `ReportPhotoJson[]` (detail) so the visual language is
//     identical across surfaces.
//   - Reuses the existing photo-status label mapping
//     (`photoStatusLabel`) so wire enums never leak into user-facing
//     copy — same contract as `PhotosScreen.tsx`.
//   - Renders a `<canvas>`-derived data URL or the existing
//     `photosApi.thumbnailUrl()` proxy for committed photos. The
//     thumbnail GET path is unchanged from the existing read
//     pipeline; this component is purely a presentation layer.
//
// Why not refactor `PhotosScreen` and the inline grid in
// `ReportDetailScreen` to use this component:
//   - The existing `photo-grid` / `photo-tile` CSS is the
//     contract the staging visual regression covers. Keeping this
//     component's root markup as `.photo-grid > .photo-tile`
//     lets the editor share styling without changing the Detail
//     surface's DOM structure.
//   - Phase 1 keeps the integration scope narrow: the editor gains
//     a `PhotoStrip`; the Detail screen continues to use its
//     inline grid (which is the same DOM shape) until a later
//     phase decides to consolidate.

import { useEffect, useRef, useState } from 'react';
import type { ReportPhotoJson } from '@/api/types';
import { photosApi } from '@/api/reportsApi';
import { PHOTO_STATUS_LABEL, isVisiblePhoto, photoUiStatusFor } from './photoStatusLabel';
import type { UploadTask } from './photoUpload/photoUploadTypes';

export interface PhotoStripProps {
  /** Committed photos (uploaded rows from the Backend). */
  photos: ReportPhotoJson[];
  /** In-flight uploads from the editor's controller. */
  tasks?: UploadTask[];
  /** Bearer-token provider (only used when `photos` is non-empty). */
  getToken: () => string | null;
  /** Retry callback (only meaningful for `tasks` in `failed` state). */
  onRetry?: (taskId: string) => void;
  /** Delete callback for committed photos. Only called for uploaded photos. */
  onDelete?: (photoId: string) => void;
  /** View-original callback for committed photos. Opens the full-res viewer. */
  onViewOriginal?: (photo: ReportPhotoJson) => void;
  /** Header copy. Editor uses "Photos (N)"; Detail uses "Photos (N)". */
  title?: string;
}

export function PhotoStrip(props: PhotoStripProps) {
  const { photos, tasks = [], getToken, onRetry, onDelete, onViewOriginal, title } = props;

  const visiblePhotos = photos.filter(isVisiblePhoto);
  const total = visiblePhotos.length + tasks.length;

  const heading = title ?? (total === 0 ? 'No photos attached' : `Photos (${total})`);

  return (
    <section className="photo-strip">
      <h3 style={{ marginTop: 0 }}>{heading}</h3>
      {total === 0 ? (
        <div className="empty">No photos attached.</div>
      ) : (
        <div className="photo-grid">
          {/* Committed tiles first (chronological). The Detail screen
              already orders by sort_order + created_at; we trust the
              caller to pass the list in the right order. */}
          {visiblePhotos.map((p) => (
            <CommittedTile
              key={`committed-${p.id}`}
              photo={p}
              getToken={getToken}
              onDelete={onDelete ? () => onDelete(p.id) : undefined}
              onViewOriginal={onViewOriginal ? () => onViewOriginal(p) : undefined}
            />
          ))}
          {/* In-flight tiles last so the user sees upload progress
              in the spot they tapped "Add Photo" in. */}
          {tasks.map((t) => (
            <TaskTile
              key={`task-${t.taskId}`}
              task={t}
              onRetry={onRetry ? () => onRetry(t.taskId) : undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface CommittedTileProps {
  photo: ReportPhotoJson;
  getToken: () => string | null;
  onDelete?: () => void;
  onViewOriginal?: () => void;
}

function CommittedTile({ photo, getToken, onDelete, onViewOriginal }: CommittedTileProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    async function load() {
      if (!photo.thumbnail_r2_key) return;
      try {
        const u = await photosApi.fetchAsset(
          getToken,
          photosApi.thumbnailUrl(photo.report_id, photo.id),
        );
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      } catch {
        // Swallow — render placeholder.
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [photo, getToken]);

  // Keyboard trap inside confirm dialog: Escape closes.
  useEffect(() => {
    if (!showConfirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowConfirm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showConfirm]);

  // Focus the Cancel button when dialog opens.
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showConfirm) {
      // Defer to allow the DOM to paint first.
      const t = window.setTimeout(() => cancelBtnRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [showConfirm]);

  const label = PHOTO_STATUS_LABEL[photoUiStatusFor(photo.upload_status)];

  const handleTileClick = () => {
    if (onViewOriginal) onViewOriginal();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(false);
    onDelete?.();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) setShowConfirm(false);
  };

  return (
    <>
      <div
        className={`photo-tile${onViewOriginal ? ' photo-tile--clickable' : ''}`}
        onClick={handleTileClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && onViewOriginal) onViewOriginal();
        }}
        aria-label="View original photo"
      >
        {url ? (
          <img src={url} alt="Report photo" />
        ) : (
          <div className="empty" style={{ padding: 8, fontSize: 12 }}>
            {label}
          </div>
        )}

        {/* Top-right × delete button — only rendered when onDelete is provided */}
        {onDelete && (
          <button
            type="button"
            className="photo-tile__delete-btn"
            onClick={handleDeleteClick}
            aria-label="Delete photo"
            title="Delete photo"
          >
            ×
          </button>
        )}

        <div className="photo-tile__caption" data-status={photoUiStatusFor(photo.upload_status)}>
          {label}
        </div>
      </div>

      {/* Confirm dialog — rendered outside the tile so overflow/positioning is not an issue */}
      {showConfirm && (
        <div
          className="photo-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="photo-confirm-title"
          onClick={handleBackdropClick}
          ref={dialogRef}
        >
          <div className="photo-confirm-box">
            <h4 id="photo-confirm-title">Delete photo?</h4>
            <p>Are you sure you want to delete this photo? This action cannot be undone.</p>
            <div className="photo-confirm-actions">
              <button
                type="button"
                className="btn"
                ref={cancelBtnRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirm(false);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn--danger" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface TaskTileProps {
  task: UploadTask;
  onRetry?: () => void;
}

function TaskTile({ task, onRetry }: TaskTileProps) {
  // Use an object URL for the generated thumbnail Blob so the
  // browser releases it when the task is removed.
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!task.thumbnail) {
      setThumbUrl(null);
      return;
    }
    const u = URL.createObjectURL(task.thumbnail.blob);
    setThumbUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [task.thumbnail]);

  const label =
    task.status === 'pending'
      ? 'Waiting to upload'
      : task.status === 'uploading'
        ? 'Uploading…'
        : task.status === 'uploaded'
          ? 'In cloud'
          : 'Upload failed';

  const showThumb = task.status === 'uploaded' || task.status === 'failed';
  const showProgress = task.status === 'pending' || task.status === 'uploading';

  return (
    <div className="photo-tile photo-tile--task">
      {showThumb && thumbUrl ? (
        <img src={thumbUrl} alt="Report photo" />
      ) : showProgress ? (
        <div className="photo-tile__progress" aria-live="polite">
          {task.status === 'pending' ? 'Queued' : 'Uploading…'}
        </div>
      ) : (
        <div className="empty" style={{ padding: 8, fontSize: 12 }}>
          {label}
        </div>
      )}
      <div
        className="photo-tile__caption"
        data-status={task.status === 'uploading' ? 'syncing' : task.status}
      >
        {label}
        {task.status === 'failed' && onRetry ? (
          <>
            {' · '}
            <button
              type="button"
              className="photo-tile__retry"
              onClick={onRetry}
              aria-label="Retry photo upload"
            >
              Retry
            </button>
          </>
        ) : null}
        {task.status === 'failed' && task.error ? (
          <div className="photo-tile__error" role="status">
            {task.error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
